import React, { useState, useEffect, useRef, useCallback } from "react";
import { Base64 } from "js-base64";

import "./ChatAssistant.css";
import VoiceAssistant from "./VoiceAssistant";
import LinkRenderer from "./LinkRenderer";
// Using Unicode fullscreen symbol

const CHAT_API_ENDPOINT =
  "https://ekauvkrdgj.us-east-1.awsapprunner.com/process-message-staging";

const ENABLE_MESSAGE_FORMATTING = false;

const getAuthToken = () => {
  const credentials = "retell_api_user:*$428JF$S@/e";
  return `Basic ${Base64.encode(credentials)}`;
};

const ChatAssistant = ({
  title = "PatienTrials",
  apiEndpoint = CHAT_API_ENDPOINT,
}) => {
  const [messages, setMessages] = useState([
    // {
    //   id: Date.now(),
    //   role: "agent",
    //   content:
    //     "Welcome! I'm your clinical trials assistant. How can I help you today?",
    //   timestamp: new Date().toISOString(),
    // },
    // {
    //   id: Date.now(),
    //   role: "user",
    //   content: "Hello, how are you?",
    //   timestamp: new Date().toISOString(),
    // },
    // {
    //   id: Date.now(),
    //   role: "agent",
    //   content: "https://apps.apple.com/us/app/patientrials/id1386748565",
    //   timestamp: new Date().toISOString(),
    // },
    // {
    //   id: Date.now(),
    //   role: "agent",
    //   content:
    //     "https://play.google.com/store/apps/details?id=com.trials.me.participant",
    //   timestamp: new Date().toISOString(),
    // },
    // {
    //   id: Date.now(),
    //   role: "agent",
    //   content:
    //     "https://support.qr-code-generator.com/hc/en-us/articles/7664245616653-Why-should-I-create-Dynamic-Codes",
    //   timestamp: new Date().toISOString(),
    // },
  ]);
  const [inputMessage, setInputMessage] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [chatId, setChatId] = useState(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);

  const focusInputWithoutScroll = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;

    // Preserve the page scroll position; focusing can cause the browser to scroll
    // the page to the focused element (especially when the chat is near the bottom).
    const x = window.scrollX;
    const y = window.scrollY;

    try {
      // Modern browsers
      el.focus({ preventScroll: true });
    } catch (e) {
      // Fallback
      el.focus();
    } finally {
      // Extra safety: if the browser scrolled anyway, restore.
      window.scrollTo(x, y);
    }
  }, []);

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      // Use requestAnimationFrame for smoother scrolling with a small delay
      requestAnimationFrame(() => {
        setTimeout(() => {
          messagesContainerRef.current.scrollTo({
            top: messagesContainerRef.current.scrollHeight,
            behavior: "smooth",
          });
        }, 50);
      });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Focus the input on mount without jumping the page
  useEffect(() => {
    focusInputWithoutScroll();
  }, [focusInputWithoutScroll]);

  // Format message using ChatGPT via backend API
  const formatMessage = async (content) => {
    if (!ENABLE_MESSAGE_FORMATTING) return content;
    try {
      const response = await fetch("/backend/format-message", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ message: content }),
      });

      if (!response.ok) {
        console.error("Failed to format message");
        return content; // Return original message if formatting fails
      }

      const data = await response.json();
      return data.formattedMessage || content;
    } catch (error) {
      console.error("Error formatting message:", error);
      return content; // Return original message if formatting fails
    }
  };

  // Send message to the chat API
  const sendMessage = async (message, chatId) => {
    if (!message.trim()) return;

    // Add user message to chat
    const userMessage = {
      id: Date.now(),
      role: "user",
      content: message.trim(),
      timestamp: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMessage]);

    setInputMessage("");
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(apiEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: getAuthToken(),
        },
        body: JSON.stringify({
          message: message.trim(),
          chatId: chatId, // null for first message
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const res = await response.json();

      if (res.chat_status === "ended") {
        setChatId(null);
        if (res.message.length === 0) {
          setMessages((prev) => [...prev.slice(0, -1)]);
          sendMessage(message, null);
          return;
        }
      } else {
        setChatId(res.chatId);
      }
      if (res.message.length > 0) {
        // Format all messages in parallel for better performance
        const formattingPromises = res.message.map((message, index) =>
          formatMessage(message.content).then((formattedContent) => ({
            id: message.message_id,
            role: "agent",
            content: formattedContent,
            timestamp: new Date(message.created_timestamp).toISOString(),
            originalIndex: index, // Preserve original order
          }))
        );

        // Wait for all messages to be formatted
        const formattedMessages = await Promise.all(formattingPromises);

        // Sort by original index to ensure correct order (in case promises resolve out of order)
        formattedMessages.sort((a, b) => a.originalIndex - b.originalIndex);

        // Add messages to chat in order with staggered delays
        formattedMessages.forEach((assistantMessage, index) => {
          setTimeout(() => {
            setMessages((prev) => {
              // Simply append the message - order is maintained by sorting
              return [...prev, assistantMessage];
            });
          }, index * 1000);
        });
      }
    } catch (err) {
      setError(`Failed to send message. Please try again.`);
      setInputMessage(message);
      setMessages((prev) => [...prev.slice(0, -1)]);
      console.error("Error sending message:", err);
    } finally {
      setIsLoading(false);
      setTimeout(() => focusInputWithoutScroll(), 200);
    }
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    if (inputMessage.trim() && !isLoading) {
      sendMessage(inputMessage, chatId);
    }
  };

  // Handle input change
  const handleInputChange = (e) => {
    setInputMessage(e.target.value);
  };

  // // Clear chat
  // const clearChat = () => {
  //   setMessages([]);
  //   setChatId(null);
  //   setError(null);
  // };

  // Format timestamp for display
  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div
      className={`chat-assistant-container ${
        isExpanded ? "expanded-chat" : ""
      }`}
    >
      <div className="chat-header">
        <div className="chat-title">
          {/* <h3>{title}</h3> */}
          <div className="status-indicator">
            <div
              className={`status-dot ${isLoading ? "loading" : "connected"}`}
            ></div>
            <span>{isLoading ? "Typing..." : "Online"}</span>
          </div>
        </div>
        <div className="chat-controls">
          <button
            className="expand-button"
            onClick={() => setIsExpanded((prev) => !prev)}
            title={isExpanded ? "Restore" : "Expand"}
          >
            {isExpanded ? (
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="fullscreen-svg"
              >
                <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" />
              </svg>
            ) : (
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="fullscreen-svg"
              >
                <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
              </svg>
            )}
          </button>
        </div>
      </div>

      <div className="chat-messages" ref={messagesContainerRef}>
        {messages.length === 0 && !isLoading && (
          <div className="welcome-message">
            <p>
              Welcome! I'm your clinical trials assistant. How can I help you
              today?
            </p>
          </div>
        )}

        {messages.map((message) => (
          <div key={message.id} className={`message ${message.role}`}>
            <div className="message-content">
              <div className="message-text">
                <LinkRenderer content={message.content} />
              </div>
              <div className="message-time">
                {formatTime(message.timestamp)}
              </div>
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="message agent">
            <div className="message-content">
              <div className="message-text">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {error && (
        <div className="error-message">
          <p>{error}</p>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      <div className="chat-footer">
        <form onSubmit={handleSubmit} className="message-form">
          <div className="input-container">
            <input
              ref={inputRef}
              type="text"
              value={inputMessage}
              onChange={handleInputChange}
              placeholder="Type your message..."
              disabled={isLoading}
              className="message-input"
            />
            <button
              type="submit"
              disabled={!inputMessage.trim() || isLoading}
              className="send-button"
            >
              {isLoading ? "..." : "Send"}
            </button>
            <div>
              <VoiceAssistant />
            </div>
          </div>
        </form>

        <div className="terms-notice">
          <p>
            By using chat or voice assistant, you agree to our{" "}
            <a
              href="https://app.termly.io/document/terms-of-use-for-saas/c82c310a-9a7f-435e-88d7-2d7fafc9ecef"
              target="_blank"
              rel="noopener noreferrer"
              className="terms-link"
            >
              Terms
            </a>{" "}
            and have read our{" "}
            <a
              href="https://app.termly.io/document/privacy-policy/1dafd99f-d664-43fa-873a-d046293e77c2"
              target="_blank"
              rel="noopener noreferrer"
              className="privacy-link"
            >
              Privacy Policy
            </a>
            .
          </p>
        </div>
      </div>
    </div>
  );
};

export default ChatAssistant;
